import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  useStartUnwinder,
  useStopUnwinder,
  useUnwinderState,
  useClearUnwinderPanicLockout,
} from '../../api/useUnwinding';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { JobProgressStepper } from 'src/Common/components/JobProgressStepper/JobProgressStepper';

export const StopStartUnwinderButtons = () => {
  const { data: state } = useUnwinderState();
  const stopMutation = useStopUnwinder();
  const startMutation = useStartUnwinder();
  const clearLockoutMutation = useClearUnwinderPanicLockout();

  const isRunning = state?.isRunning ?? true;
  // While a 24h panic lockout is armed, START / rule activation are refused by
  // the control plane — disable START and surface a clear-lockout control.
  const lockout = state?.panicLockout;
  const isLocked = Boolean(lockout?.armed);
  const isBusy =
    stopMutation.isLoading ||
    startMutation.isLoading ||
    clearLockoutMutation.isLoading;

  // Shared system/job stepper — only show entries from the unwinder subsystem
  const { currentJob } = useUpdates();
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === 'unwinder' &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt && Date.now() - currentJob.completedAt < 5000))
  );

  // Keep the stepper visible for ~4s after completion, then hide
  const [showJobResult, setShowJobResult] = useState(false);
  const prevJobRunning = useRef(false);
  useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const showProgress = (isMyJob || showJobResult) && currentJob;

  const stopRunning =
    isMyJob && currentJob?.type === 'stop' && currentJob?.status === 'running';
  const startRunning =
    isMyJob && currentJob?.type === 'start' && currentJob?.status === 'running';

  // Compact status chip + transient result snackbar so the operator always has
  // an at-a-glance signal even if the stepper popover lags or auto-dismisses.
  // The stepper depends on the system-job poll loop (every 2s) seeded by the
  // mutation; `useUnwinderState` polls /unwinder/state every 3s independently
  // and is the source of truth for "did the pod actually scale down".
  const status: 'running' | 'stopping' | 'starting' | 'stopped' = stopRunning
    ? 'stopping'
    : startRunning
      ? 'starting'
      : isRunning
        ? 'running'
        : 'stopped';

  const statusLabel = {
    running: 'Running',
    stopping: 'Stopping…',
    starting: 'Starting…',
    stopped: 'Stopped'
  }[status];

  const statusColor: 'success' | 'warning' | 'info' | 'default' = {
    running: 'success',
    stopping: 'warning',
    starting: 'info',
    stopped: 'default'
  }[status] as 'success' | 'warning' | 'info' | 'default';

  const [resultSnack, setResultSnack] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);

  // Watch for job completion AFTER it was mine. The popover already shows
  // step-by-step progress; this is the "did it actually finish" toast that
  // survives even if the popover auto-dismisses or the user has navigated.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (
      currentJob &&
      currentJob.subsystem === 'unwinder' &&
      prev === 'running' &&
      (currentJob.status === 'completed' || currentJob.status === 'failed')
    ) {
      const verb = currentJob.type === 'stop' ? 'stop' : 'start';
      if (currentJob.status === 'completed') {
        setResultSnack({
          kind: 'success',
          message: `Unwinder ${verb} completed${
            currentJob.triggeredPanic ? ' (panic was triggered)' : ''
          }.`
        });
      } else {
        setResultSnack({
          kind: 'error',
          message: `Unwinder ${verb} failed: ${currentJob.error ?? 'unknown error'}`
        });
      }
    }
    prevStatusRef.current = currentJob?.status;
  }, [currentJob]);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip
            title={
              status === 'stopping'
                ? 'Unwinder is stopping — pod is being scaled to 0'
                : status === 'starting'
                  ? 'Unwinder is starting — waiting for pod Ready'
                  : status === 'running'
                    ? `Unwinder pod running${
                        typeof state?.replicas === 'number'
                          ? ` (${state.replicas} replica${state.replicas === 1 ? '' : 's'})`
                          : ''
                      }`
                    : `Unwinder stopped${
                        state?.lastStopAt
                          ? ` ${new Date(state.lastStopAt).toLocaleTimeString()}`
                          : ''
                      }`
            }
            placement="top"
          >
            <Chip
              size="small"
              label={statusLabel}
              color={statusColor}
              variant={status === 'stopped' ? 'outlined' : 'filled'}
              icon={
                status === 'stopping' || status === 'starting' ? (
                  <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
                ) : undefined
              }
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
          <Tooltip
            title={
              stopRunning
                ? 'Stopping unwinder…'
                : !isRunning
                  ? 'Unwinder already stopped'
                  : 'Stop position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  stopMutation.isLoading || stopRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PowerSettingsNewIcon />
                  )
                }
                onClick={() => stopMutation.mutate()}
                disabled={isBusy || !isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {stopRunning ? 'Stopping' : 'Stop Unwinder'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isLocked
                ? `Under 24h panic lockout${
                    lockout?.expiresAt
                      ? ` until ${new Date(lockout.expiresAt).toLocaleString()}`
                      : ''
                  } — clear the lockout to start`
                : startRunning
                  ? 'Starting unwinder…'
                  : isRunning
                    ? 'Unwinder already running'
                    : 'Start position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                  startMutation.isLoading || startRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => startMutation.mutate()}
                disabled={isBusy || isRunning || isLocked || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {startRunning ? 'Starting' : 'Start Unwinder'}
              </Button>
            </span>
          </Tooltip>
          {isLocked && (
            <Tooltip
              title={
                `Unwinder is under a 24h panic lockout` +
                `${lockout?.armedAt ? ` (armed ${new Date(lockout.armedAt).toLocaleString()})` : ''}` +
                `. Clearing it re-enables Start + rule activation.`
              }
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={
                    clearLockoutMutation.isLoading ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <LockOpenIcon />
                    )
                  }
                  onClick={() => clearLockoutMutation.mutate()}
                  disabled={clearLockoutMutation.isLoading}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Clear lockout
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        {isLocked && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              color="error"
              variant="outlined"
              label={`24h panic lockout${
                lockout?.expiresAt
                  ? ` — expires ${new Date(lockout.expiresAt).toLocaleTimeString()}`
                  : ''
              }`}
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          </Box>
        )}

        {/* Progress popover */}
        {showProgress && currentJob && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: 1,
              zIndex: 1400
            }}
          >
            <Paper elevation={6} sx={{ p: 1.5, minWidth: 280, maxWidth: 340, bgcolor: 'background.paper' }}>
              <JobProgressStepper job={currentJob} label="Unwinder" />
            </Paper>
          </Box>
        )}
      </Box>

      <Snackbar
        open={Boolean(resultSnack)}
        autoHideDuration={6000}
        onClose={() => setResultSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {resultSnack ? (
          <Alert
            severity={resultSnack.kind}
            onClose={() => setResultSnack(null)}
            sx={{ fontSize: '0.8rem' }}
          >
            {resultSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};
